import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import toast from "react-hot-toast";
import {
  ButtonComp,
  CardComp,
  TableCompData,
} from "../../../components";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import ConfirmationModal from "../../../components/Common/ConfirmationModal";
import FilterFind from "../../../components/FilterFind";
import {
  useDeleteMigratedLoanDraftMutation,
  useGetMigratedLoansQuery,
} from "../../../store/admin/loan";
import { convertToTitleCase } from "../../../utils/helpers";
import { formatMoney } from "../../../utils/currencyFormat";

export default function RecordExistingLoan() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [lockStatus, setLockStatus] = useState("all");
  const [draftToDelete, setDraftToDelete] = useState(null);

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetMigratedLoansQuery({
      page,
      limit: 20,
      lock_status: lockStatus,
    });
  const [deleteDraft, { isLoading: isDeleting }] =
    useDeleteMigratedLoanDraftMutation();

  const rows = Array.isArray(data?.company_loan_requests)
    ? data.company_loan_requests
    : [];
  const pagination = data?.pagination || {};

  const isDraftRow = (row) =>
    Boolean(row?.migration_draft) ||
    (Boolean(row?.is_migrated) && !row?.migration_locked_at);

  const handleDeleteDraft = async () => {
    if (!draftToDelete?.id) return;
    try {
      const result = await deleteDraft(draftToDelete.id).unwrap();
      toast.success(
        result?.message || "Migration draft deleted."
      );
      setDraftToDelete(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Could not delete draft");
    }
  };

  const columns = [
    {
      name: "MEMBER",
      selector: (row) => {
        const m = row?.member;
        if (!m) return "—";
        if (typeof m === "string") return m;
        return (
          `${m.first_name || ""} ${m.last_name || ""}`.trim() ||
          m.name ||
          "—"
        );
      },
    },
    {
      name: "MEMBERSHIP NO",
      selector: (row) =>
        row?.member?.member_no || row?.member?.member?.member_no || "—",
    },
    {
      name: "LOAN TYPE",
      selector: (row) => {
        const name = row?.loan_type?.name || row?.loan_type_name || "—";
        const model = String(
          row?.loan_model || row?.loan_type?.loan_model || ""
        ).toLowerCase();
        return model === "informal" ? `${name} (Flexible)` : name;
      },
    },
    {
      name: "AMOUNT",
      selector: (row) =>
        formatMoney(Number(row?.amount || 0).toFixed(2)),
    },
    {
      name: "DISBURSED",
      selector: (row) =>
        row?.disbursed_at
          ? moment(row.disbursed_at).format("DD-MMM-YYYY")
          : "—",
    },
    {
      name: "STATUS",
      cell: (row) => {
        const draft = row?.migration_draft || !row?.migration_locked_at;
        return (
          <span
            className={`badge rounded-pill px-3 py-2 ${
              draft ? "bg-warning text-dark" : "bg-success"
            }`}
          >
            {draft ? "Migration draft" : "Locked"}
          </span>
        );
      },
    },
    {
      name: "LOAN STATUS",
      cell: (row) => (
        <span className="badge rounded-pill px-3 py-2 bg-secondary">
          {convertToTitleCase(row?.status || "—")}
        </span>
      ),
    },
    {
      name: "ACTION",
      cell: (row) => {
        const draft = isDraftRow(row);
        return (
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <ButtonComp
              btnText={draft ? "Continue" : "View"}
              btnClassName="bg-secondary2 text-white rounded-pill px-3 py-1"
              style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
              onClick={() =>
                navigate(
                  `/super_admin/loan_management/record_existing_loan/${row.id}`
                )
              }
            />
            {draft && (
              <ButtonComp
                btnText="Delete"
                btnClassName="bg-danger text-white rounded-pill px-3 py-1"
                style={{ whiteSpace: "nowrap", minWidth: "fit-content" }}
                onClick={() => setDraftToDelete(row)}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <section className="px-lg-3 min-vh-100 pb-5 mb-3">
      <FilterFind rightSide={false} title="Record Existing Loan" />
      <p className="text-muted px-1 mb-3" style={{ fontSize: 14 }}>
        Bring loans that started before UniqueCoop onto the platform. Members
        only see a loan after you Confirm &amp; lock.
      </p>

      <CardComp
        cardClassName="border-0 rounded-10 px-lg-4 mb-4 pt-4 shadow3"
        cardBodyClassName="py-3"
      >
        <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between mb-3">
          <div style={{ minWidth: 220 }}>
            <label className="fw-bold text-black1 mb-1 d-block">Show</label>
            <select
              className="form-select"
              value={lockStatus}
              onChange={(e) => {
                setLockStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="unlocked">Drafts (not locked)</option>
              <option value="locked">Locked</option>
              <option value="all">All migrated</option>
            </select>
          </div>
          <ButtonComp
            btnText="Record new existing loan"
            btnClassName="bg-primary1 text-white rounded px-4 py-2"
            onClick={() =>
              navigate("/super_admin/loan_management/record_existing_loan/new")
            }
          />
        </div>

        {isError && (
          <div className="alert alert-danger">
            {error?.data?.message || error?.message || "Could not load migrated loans."}
            <button className="btn btn-link" type="button" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        )}

        <TableCompData
          columns={columns}
          data={rows}
          progressPending={isLoading || isFetching}
          progressComponent={<TableSkeletonLoader rows={8} columns={8} />}
          pagination
          paginationServer
          paginationTotalRows={pagination.total || 0}
          onChangePage={(p) => setPage(p)}
          paginationDefaultPage={page}
        />
      </CardComp>

      <ConfirmationModal
        isOpen={Boolean(draftToDelete)}
        onClose={() => setDraftToDelete(null)}
        title="Delete migration draft?"
        message="This permanently removes the draft loan, its schedule, and any historical payments recorded for migration. Locked loans cannot be deleted here."
        confirmText={isDeleting ? "Deleting…" : "Delete draft"}
        confirmButtonClass="bg-danger text-white"
        onConfirm={handleDeleteDraft}
        isLoading={isDeleting}
      />
    </section>
  );
}
